// PRE-SIGNUP-QUESTION: Standalone premium 3-slide teaser shown before signup.
// Fully self-contained — does NOT use EnhancedLearnMode or any shared slide renderer.
//   Slide 1 (buildup):   "CBSE tests competence now — try one"
//   Slide 2 (question):  compact weather card + question + 4 options (gated)
//   Slide 3 (lesson):    answer + explanation + number-line visual
// On the final CTA, the parent routes unauthenticated users straight to the signup modal.

import { useState } from "react";
import { getFontFamily } from "../constants/colors";

const Q = {
  en: {
    cardLabel: "RAIN FORECAST · TOMORROW",
    cities: [
      { name: "Mumbai", value: "0.85", error: false },
      { name: "Delhi", value: "1.0", error: false },
      { name: "Bengaluru", value: "0.3", error: false },
      { name: "Chennai", value: "1.4", error: true },
    ],
    options: [
      "1.4 is a high number — algorithm assumes error",
      "Probabilities must lie between 0 and 1",
      "The app can't display values greater than 1.0",
      "Chennai is too far for accurate forecasts",
    ],
    correct: 1,
    trap: 0,
    rule: "Probability of any event must lie between 0 and 1, inclusive. 1.4 breaks this rule, so the app flags it.",
    whyWrong: [
      "Right conclusion, wrong reason. The math rule (0 ≤ P ≤ 1) is what makes 1.4 invalid — not the algorithm's view of 'high numbers'.",
      null,
      "Not the reason. The app could display 2.0 if it wanted — but 2.0 still wouldn't be a valid probability. The cap is mathematical, not a display limit.",
      "Distance affects accuracy, not validity. A far-away forecast of 0.8 would still be a valid (if uncertain) probability.",
    ],
  },
  hi: {
    cardLabel: "बारिश का अंदाज़ा · कल",
    cities: [
      { name: "Mumbai", value: "0.85", error: false },
      { name: "Delhi", value: "1.0", error: false },
      { name: "Bengaluru", value: "0.3", error: false },
      { name: "Chennai", value: "1.4", error: true },
    ],
    options: [
      "1.4 high है — algorithm error मान रहा",
      "Probability हमेशा 0 और 1 के बीच होती है",
      "App 1.0 से ज़्यादा value दिखा नहीं सकता",
      "Chennai forecast के लिए बहुत दूर है",
    ],
    correct: 1,
    trap: 0,
    rule: "किसी भी event की probability 0 और 1 के बीच होनी चाहिए। 1.4 इस rule को break करता है, इसलिए app flag करता है।",
    whyWrong: [
      "Conclusion सही, reason गलत। Math rule (0 ≤ P ≤ 1) वजह है — algorithm की opinion नहीं।",
      null,
      "यह reason नहीं है। App 2.0 भी दिखा सकता है, लेकिन 2.0 valid probability फिर भी नहीं होता।",
      "Distance accuracy को affect करता है, validity को नहीं। दूर का 0.8 forecast भी valid probability है।",
    ],
  },
};

const S = {
  en: {
    skip: "Skip",
    signIn: "Sign in",

    buildupEyebrow: "BOARD QUESTION STYLE",
    buildupThenLabel: "THEN",
    buildupThenText: "Find x in 2x = 14",
    buildupNowLabel: "NOW",
    buildupNowText: "Why is Chennai 1.4 flagged as ERROR?",
    buildupTitle: "Same chapter. Very different question.",
    buildupSubBefore: "CBSE now tests ",
    buildupSubAccent: "competence",
    buildupSubAfter: " not rote learning.",
    buildupCta: "Show me one →",

    questionEyebrow: "COMPETENCE QUESTION",
    questionTitle: "Why is the probability of rain in Chennai flagged as ERROR?",
    questionCtaGated: "Tap an answer",
    questionCtaActive: "See the answer →",

    lessonEyebrowCorrect: "EXACTLY",
    lessonEyebrowTrap: "ALMOST",
    lessonEyebrowWrong: "NOT QUITE",
    lessonTitle: "Probabilities live between 0 and 1",
    lessonCta: "Sign up free →",
    yourAnswerLabel: "YOUR ANSWER",
    correctAnswerLabel: "CORRECT ANSWER",

    stepLabel: (n) => `${n} of 3 · Probability · Class 10`,
  },
  hi: {
    skip: "छोड़ें",
    signIn: "लॉगिन",

    buildupEyebrow: "बोर्ड के सवाल",
    buildupThenLabel: "पहले",
    buildupThenText: "Find x in 2x = 14",
    buildupNowLabel: "अब",
    buildupNowText: "Chennai 1.4 पर ERROR क्यों?",
    buildupTitle: "एक ही chapter। बहुत अलग सवाल।",
    buildupSubBefore: "CBSE अब ",
    buildupSubAccent: "competence",
    buildupSubAfter: " test करता है rote learning नहीं।",
    buildupCta: "एक दिखाओ →",

    questionEyebrow: "COMPETENCE QUESTION",
    questionTitle: "Chennai में बारिश की probability पर ERROR क्यों?",
    questionCtaGated: "जवाब चुनो",
    questionCtaActive: "जवाब देखो →",

    lessonEyebrowCorrect: "बिलकुल सही",
    lessonEyebrowTrap: "लगभग",
    lessonEyebrowWrong: "नहीं",
    lessonTitle: "Probability हमेशा 0 और 1 के बीच",
    lessonCta: "Free sign up →",
    yourAnswerLabel: "तुम्हारा जवाब",
    correctAnswerLabel: "सही जवाब",

    stepLabel: (n) => `3 में से ${n} · Probability · Class 10`,
  },
};

const ANIM_CSS = `
@keyframes psq-fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes psq-fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

// Compact weather card — used inline on the question slide.
const WeatherCardCompact = ({ t, delay = 0.14 }) => (
  <div style={{
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12,
    padding: "10px 12px 4px",
    marginBottom: 16,
    animation: `psq-fadeUp 0.5s ${delay}s ease both`,
  }}>
    <div style={{
      fontSize: 9, fontWeight: 700,
      color: "rgba(255,255,255,0.45)",
      letterSpacing: "0.16em",
      marginBottom: 6,
    }}>
      {t.cardLabel}
    </div>
    {t.cities.map((city, i) => (
      <div key={i} style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "6px 0",
        borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.07)",
      }}>
        <span style={{
          fontSize: 13, fontWeight: 500,
          color: city.error ? "#FFC6B5" : "rgba(255,255,255,0.92)",
        }}>
          {city.name}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontSize: 13, fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: city.error ? "#FFC6B5" : "rgba(255,255,255,0.94)",
          }}>
            {city.value}
          </span>
          {city.error && (
            <span style={{
              fontSize: 8, fontWeight: 800,
              letterSpacing: "0.12em",
              color: "#FFC6B5",
              border: "1px solid rgba(255,198,181,0.45)",
              borderRadius: 4,
              padding: "1px 5px",
              background: "rgba(255,198,181,0.08)",
            }}>
              ERROR
            </span>
          )}
        </span>
      </div>
    ))}
  </div>
);

export function PreSignupQuestion({ lang = "en", onContinue, onSkip, onSignIn }) {
  const [step, setStep] = useState(0); // 0 = buildup, 1 = question, 2 = lesson
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);

  const ff = getFontFamily(lang);
  const t = Q[lang === "hi" ? "hi" : "en"];
  const s = S[lang === "hi" ? "hi" : "en"];

  const isCorrect = answered && selected === t.correct;
  const isTrap = answered && selected === t.trap;

  const handleTap = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswered(true);
  };

  const handleNext = () => {
    if (step === 0) setStep(1);
    else if (step === 1 && answered) setStep(2);
    else if (step === 2) onContinue?.();
  };

  const canAdvance = step === 0 || (step === 1 && answered) || step === 2;

  const ctaText =
    step === 0 ? s.buildupCta
    : step === 1 ? (answered ? s.questionCtaActive : s.questionCtaGated)
    : s.lessonCta;

  const lessonEyebrow = isCorrect ? s.lessonEyebrowCorrect : isTrap ? s.lessonEyebrowTrap : s.lessonEyebrowWrong;

  return (
    <div style={{
      minHeight: "100dvh",
      background: "linear-gradient(165deg, #1A0A06 0%, #3D1A0F 30%, #5A2418 60%, #843A28 100%)",
      fontFamily: ff,
      display: "flex", flexDirection: "column",
      color: "white",
      WebkitFontSmoothing: "antialiased",
    }}>
      <style>{ANIM_CSS}</style>

      {/* Top-right escape strip */}
      <div style={{
        position: "fixed", top: 12, right: 12, zIndex: 100,
        display: "flex", gap: 8,
      }}>
        <button onClick={onSkip} style={{
          padding: "6px 14px", borderRadius: 999,
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "rgba(255,255,255,0.82)", fontSize: 12, fontWeight: 600,
          cursor: "pointer", fontFamily: ff,
        }}>
          {s.skip}
        </button>
        <button onClick={onSignIn} style={{
          padding: "6px 14px", borderRadius: 999,
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "rgba(255,255,255,0.95)", fontSize: 12, fontWeight: 600,
          cursor: "pointer", fontFamily: ff,
        }}>
          {s.signIn}
        </button>
      </div>

      {/* Slide content — keyed by step to retrigger entrance animations */}
      <div key={`step-${step}`} style={{
        flex: 1,
        padding: "72px 24px 16px",
        maxWidth: 520, width: "100%", margin: "0 auto",
        display: "flex", flexDirection: "column",
        overflowY: "auto", boxSizing: "border-box",
      }}>

        {/* Slide 1 — Buildup: hero headline + THEN/NOW comparison cards */}
        {step === 0 && (
          <div style={{
            flex: 1,
            display: "flex", flexDirection: "column",
            justifyContent: "center",
            paddingBottom: 16,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: "rgba(255,255,255,0.5)",
              letterSpacing: "0.22em",
              marginBottom: 18,
              textAlign: "center",
              animation: "psq-fadeUp 0.5s ease both",
            }}>
              {s.buildupEyebrow}
            </div>

            {/* Hero headline — "competence" rendered as a serif/peach/uppercase accent;
                the surrounding "CBSE now tests" / "not rote learning." text is softened
                so the accent word carries the visual weight. */}
            <div style={{
              fontSize: 32,
              letterSpacing: "-0.028em",
              lineHeight: 1.2,
              marginBottom: 28,
              textWrap: "balance",
              textAlign: "center",
              animation: "psq-fadeUp 0.5s 0.08s ease both",
            }}>
              <span style={{
                fontSize: 20,
                fontWeight: 500,
                color: "rgba(255,255,255,0.55)",
              }}>
                {s.buildupSubBefore}
              </span>
              <span style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontWeight: 700,
                color: "#FFC6B5",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                fontStyle: "italic",
              }}>
                {s.buildupSubAccent}
              </span>
              <span style={{
                fontSize: 20,
                fontWeight: 500,
                color: "rgba(255,255,255,0.55)",
              }}>
                {s.buildupSubAfter}
              </span>
            </div>

            {/* THEN card — muted, strikethrough */}
            <div style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 14,
              padding: "14px 18px",
              animation: "psq-fadeUp 0.5s 0.16s ease both",
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800,
                color: "rgba(255,255,255,0.42)",
                letterSpacing: "0.20em",
                marginBottom: 6,
              }}>
                {s.buildupThenLabel}
              </div>
              <div style={{
                fontSize: 18, fontWeight: 500,
                color: "rgba(255,255,255,0.5)",
                letterSpacing: "-0.01em",
                fontVariantNumeric: "tabular-nums",
                textDecoration: "line-through",
                textDecorationColor: "rgba(255,255,255,0.28)",
                textDecorationThickness: "1.5px",
              }}>
                {s.buildupThenText}
              </div>
            </div>

            {/* Connector arrow */}
            <div style={{
              textAlign: "center",
              fontSize: 16,
              fontWeight: 600,
              color: "rgba(255,198,181,0.55)",
              margin: "8px 0 8px",
              letterSpacing: "0.2em",
              animation: "psq-fadeUp 0.5s 0.22s ease both",
            }}>
              ↓
            </div>

            {/* NOW card — highlighted */}
            <div style={{
              background: "rgba(255,198,181,0.08)",
              border: "1px solid rgba(255,198,181,0.32)",
              borderRadius: 14,
              padding: "14px 18px",
              animation: "psq-fadeUp 0.5s 0.28s ease both",
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800,
                color: "#FFC6B5",
                letterSpacing: "0.20em",
                marginBottom: 6,
              }}>
                {s.buildupNowLabel}
              </div>
              <div style={{
                fontSize: 19, fontWeight: 600,
                color: "white",
                letterSpacing: "-0.015em",
                lineHeight: 1.3,
              }}>
                {s.buildupNowText}
              </div>
            </div>

            {/* Takeaway line below cards */}
            <div style={{
              marginTop: 22,
              fontSize: 15, fontWeight: 600,
              color: "rgba(255,255,255,0.7)",
              letterSpacing: "-0.01em",
              lineHeight: 1.4,
              animation: "psq-fadeUp 0.5s 0.36s ease both",
            }}>
              {s.buildupTitle}
            </div>
          </div>
        )}

        {/* Slide 2 — Question */}
        {step === 1 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: "rgba(255,255,255,0.55)",
              letterSpacing: "0.18em",
              marginBottom: 14,
              animation: "psq-fadeUp 0.5s ease both",
            }}>
              {s.questionEyebrow}
            </div>
            <div style={{
              fontSize: 20, fontWeight: 700,
              color: "#FFC6B5",
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              marginBottom: 16,
              animation: "psq-fadeUp 0.5s 0.08s ease both",
            }}>
              {s.questionTitle}
            </div>

            <WeatherCardCompact t={t} delay={0.14} />

            <div style={{
              display: "flex", flexDirection: "column", gap: 8,
              animation: "psq-fadeUp 0.5s 0.20s ease both",
            }}>
              {t.options.map((opt, i) => {
                const isSelected = selected === i;
                const isAnswerRight = answered && i === t.correct;
                const isAnswerWrong = answered && isSelected && i !== t.correct;
                const isDimmed = answered && !isSelected && i !== t.correct;

                let borderColor = "rgba(255,255,255,0.16)";
                let bgColor = "rgba(255,255,255,0.04)";
                if (isAnswerRight) { borderColor = "rgba(74,222,128,0.55)"; bgColor = "rgba(74,222,128,0.08)"; }
                if (isAnswerWrong) { borderColor = "rgba(248,113,113,0.55)"; bgColor = "rgba(248,113,113,0.08)"; }

                return (
                  <div
                    key={i}
                    onClick={() => handleTap(i)}
                    style={{
                      border: `1px solid ${borderColor}`,
                      background: bgColor,
                      borderRadius: 12,
                      padding: "12px 14px",
                      cursor: answered ? "default" : "pointer",
                      transition: "all 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
                      opacity: isDimmed ? 0.42 : 1,
                      display: "flex", alignItems: "flex-start", gap: 12,
                    }}
                  >
                    <span style={{
                      flexShrink: 0,
                      width: 22, height: 22, borderRadius: 6,
                      border: `1px solid ${isAnswerRight ? "rgba(74,222,128,0.65)" : isAnswerWrong ? "rgba(248,113,113,0.65)" : "rgba(255,255,255,0.22)"}`,
                      background: isAnswerRight ? "rgba(74,222,128,0.12)" : isAnswerWrong ? "rgba(248,113,113,0.12)" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 800,
                      color: isAnswerRight ? "#86EFAC" : isAnswerWrong ? "#FCA5A5" : "rgba(255,255,255,0.65)",
                    }}>
                      {isAnswerRight ? "✓" : isAnswerWrong ? "✗" : String.fromCharCode(65 + i)}
                    </span>
                    <span style={{
                      fontSize: 13.5, fontWeight: 500,
                      color: "rgba(255,255,255,0.94)",
                      lineHeight: 1.45,
                    }}>
                      {opt}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Slide 3 — Lesson */}
        {step === 2 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 800,
              color: isCorrect ? "#86EFAC" : isTrap ? "#FFC6B5" : "#FCA5A5",
              letterSpacing: "0.18em",
              marginBottom: 14,
              animation: "psq-fadeUp 0.5s ease both",
            }}>
              {lessonEyebrow}
            </div>
            <div style={{
              fontSize: 26, fontWeight: 700,
              color: "white",
              letterSpacing: "-0.025em",
              lineHeight: 1.2,
              marginBottom: 18,
              animation: "psq-fadeUp 0.5s 0.08s ease both",
            }}>
              {s.lessonTitle}
            </div>

            {/* Your answer vs correct answer — only when wrong */}
            {!isCorrect && (
              <div style={{
                display: "flex", flexDirection: "column", gap: 8,
                marginBottom: 16,
                animation: "psq-fadeUp 0.5s 0.14s ease both",
              }}>
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "10px 12px",
                  background: "rgba(248,113,113,0.08)",
                  border: "1px solid rgba(248,113,113,0.30)",
                  borderRadius: 10,
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: "0.14em",
                    color: "#FCA5A5", flexShrink: 0, marginTop: 3,
                  }}>
                    {s.yourAnswerLabel}
                  </span>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", lineHeight: 1.4 }}>
                    {t.options[selected]}
                  </span>
                </div>
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "10px 12px",
                  background: "rgba(74,222,128,0.08)",
                  border: "1px solid rgba(74,222,128,0.30)",
                  borderRadius: 10,
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: "0.14em",
                    color: "#86EFAC", flexShrink: 0, marginTop: 3,
                  }}>
                    {s.correctAnswerLabel}
                  </span>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", lineHeight: 1.4 }}>
                    {t.options[t.correct]}
                  </span>
                </div>
              </div>
            )}

            <div style={{
              fontSize: 14, color: "rgba(255,255,255,0.9)",
              lineHeight: 1.65, fontWeight: 400,
              animation: "psq-fadeUp 0.5s 0.20s ease both",
            }}>
              {isCorrect ? t.rule : (t.whyWrong[selected] ? `${t.whyWrong[selected]} ${t.rule}` : t.rule)}
            </div>

            {/* Number line: 0 ── 1 ─✗ 1.4 */}
            <div style={{
              marginTop: 22,
              padding: "16px 6px 12px",
              animation: "psq-fadeUp 0.5s 0.26s ease both",
            }}>
              <div style={{ position: "relative", height: 4, background: "rgba(255,255,255,0.14)", borderRadius: 2 }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, height: 4,
                  width: "71%", background: "rgba(134,239,172,0.55)", borderRadius: 2,
                }} />
                <div style={{
                  position: "absolute", left: 0, top: -3, width: 10, height: 10, borderRadius: 5,
                  background: "rgba(134,239,172,0.95)", transform: "translateX(-50%)",
                }} />
                <div style={{
                  position: "absolute", left: "71%", top: -3, width: 10, height: 10, borderRadius: 5,
                  background: "rgba(134,239,172,0.95)", transform: "translateX(-50%)",
                }} />
                <div style={{
                  position: "absolute", left: "100%", top: -3, width: 10, height: 10, borderRadius: 5,
                  background: "rgba(252,165,165,0.95)", transform: "translateX(-50%)",
                  boxShadow: "0 0 0 3px rgba(248,113,113,0.18)",
                }} />
              </div>
              <div style={{
                display: "flex", justifyContent: "space-between",
                marginTop: 10, fontSize: 11, fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: "rgba(255,255,255,0.6)", letterSpacing: "0.04em",
              }}>
                <span>0 · impossible</span>
                <span style={{ marginLeft: "auto", marginRight: "auto", transform: "translateX(-30px)" }}>1 · certain</span>
                <span style={{ color: "#FCA5A5" }}>1.4 ✗</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom — dots + CTA + step label */}
      <div style={{
        flexShrink: 0,
        padding: "14px 24px 24px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(0,0,0,0.18)",
      }}>
        <div style={{
          display: "flex", justifyContent: "center", gap: 6,
          marginBottom: 14,
        }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: i === step ? 22 : 6, height: 6, borderRadius: 3,
              background: i === step ? "rgba(255,255,255,0.85)" : i < step ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.18)",
              transition: "all 0.3s ease",
            }} />
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={!canAdvance}
          style={{
            width: "100%",
            padding: "15px 0",
            borderRadius: 14,
            background: canAdvance ? "#FFFFFF" : "rgba(255,255,255,0.10)",
            color: canAdvance ? "#1A0A06" : "rgba(255,255,255,0.42)",
            fontSize: 15, fontWeight: 700,
            letterSpacing: "-0.01em",
            border: "none",
            cursor: canAdvance ? "pointer" : "not-allowed",
            fontFamily: ff,
            transition: "all 0.25s ease",
          }}
        >
          {ctaText}
        </button>
        <div style={{
          textAlign: "center",
          marginTop: 10,
          fontSize: 10, fontWeight: 600,
          letterSpacing: "0.12em",
          color: "rgba(255,255,255,0.4)",
          textTransform: "uppercase",
        }}>
          {s.stepLabel(step + 1)}
        </div>
      </div>
    </div>
  );
}
